import { Link } from 'react-router-dom';
import { ArrowRightIcon, MessageCircleIcon } from 'lucide-react';
import { HeadingText } from '../shared/HeadingText';
import { ParallaxMedia } from '../shared/ParallaxMedia';
import { PageHero } from '../shared/PageHero';
import { PressStrip, hasPress } from '../shared/PressStrip';
import { AboutShelf } from './AboutShelf';
import { VisitBlock } from '../shared/VisitBlock';
import { placeholderImages } from '../../data/placeholderImages';
import { founder, salon } from '../../data/content';
import { pageActions } from '../../data/pageActions';
import { usePages } from '../../contexts/SiteContext';
import { policyCopy } from '../../data/policies';
import { shopLabels } from '../../data/shopLabels';
import { mediaOr } from '../../core/rules/media';
import { useOverlayActions } from '../../contexts/StoreContext';
import { fill } from '../../utils/copy';
import { quietLink } from '../../ui/sections';
import { btnPrimaryLg, btnSecondaryLg } from '../../ui/styles';

/** Alleen feiten die Sarah kan onderbouwen; geen opgeklopte getallen. Reactietijd staat op Contact. */
const facts = [{
  value: policyCopy.experience,
  label: 'in de huidzorg'
}, {
  value: 'Eén',
  label: 'cliënt tegelijk, zonder haast'
}];

/**
 * Over Sarah, als één verhaal (docs/VORMGEVING.md §3). Eén vraag: waarom zou ik háár vertrouwen
 * met mijn huid? Wie (opening, sand) · hoe het begon, met feiten, opleidingen en de perslijn
 * (zelfde vlak) · waar ik voor sta (klei, het ene bewegende beeld) · haar eigen plank (shell) ·
 * kom kennismaken (blush). Geen blok herhaalt een ander.
 *
 * Alleen over Sarah: vaste cliënten staan bij de reviews (Home, productpagina), collega's op de
 * salonpagina, adres, tijden en reactietijd op Contact.
 */
export function About() {
  const pages = usePages();
  const {
    openBooking
  } = useOverlayActions();
  const copy = pages.about;
  const portrait = mediaOr(copy.hero.media, {
    image: founder.portraitPalm,
    alt: `${founder.name}, ${founder.role}`,
    focus: {
      x: 50,
      y: 30
    }
  });
  const atWork = mediaOr(copy.philosophy.media, {
    image: founder.atWork,
    alt: `${founder.name} tijdens een behandeling`
  });
  // De eerste alinea draagt de opening; de rest is "hoe het begon", met de laatste als slotzin.
  const [lead, ...rest] = copy.hero.paragraphs.filter(p => p.trim());
  const closing = rest.length > 1 ? rest[rest.length - 1] : undefined;
  const story = closing ? rest.slice(0, -1) : rest;
  const credentials = copy.credentials.items.filter(c => c.trim());
  return <>
      {/* Opening: zelfde opbouw als salon en protocollen; het eerste scherm is het portret en haar belofte. */}
      <PageHero titleId="about-title" eyebrow={copy.hero.eyebrow ? fill(copy.hero.eyebrow) : undefined} title={<HeadingText heading={copy.hero.heading} data-id="element-5562" data-mp="1r1zbbi6x5x32" />} body={lead ? fill(lead) : undefined} media={portrait} byline={{
      lead: <span className="text-ink" data-id="element-5563" data-mp="32fussxlaqhxg">{founder.name}</span>,
      sub: `${founder.role} · ${salon.city}`
    }} actions={/* Wie haar vertrouwt, wil zien wat ze kiest: de collectie is de hoofdknop, de afspraak de tweede. */
    <>
            <Link to="/webshop" className={btnPrimaryLg} data-id="element-5564" data-mp="37lzu52xjd0cp">
              {pageActions.viewCollection}
            </Link>
            {copy.hero.cta && <button type="button" onClick={() => openBooking()} aria-haspopup="dialog" className={btnSecondaryLg} data-id="element-5565" data-mp="2ysmtfet8cug2">
                {fill(copy.hero.cta)}
              </button>}
          </>} data-id="element-5561" data-mp="1m0jpvp0txok5" />

      {/* Hoe het begon: het verhaal links, het bewijs rechts. Hetzelfde zandvlak als de opening. */}
      <section className="bg-sand pb-20 sm:pb-24 lg:pb-28" aria-labelledby="about-story" data-id="element-5566" data-mp="2jucbt9ecaxm1">
        <div className="mx-auto grid max-w-content gap-12 border-t border-ink/15 px-6 pt-12 lg:grid-cols-12 lg:gap-16 lg:px-10 lg:pt-16" data-id="element-5567" data-mp="2fu0qrvi2eva1">
          <div className="max-w-2xl lg:col-span-7" data-id="element-5568" data-mp="i37m4ivyw74c">
            <h2 id="about-story" className="type-sub text-ink" data-id="element-5569" data-mp="e2fdnrux6694">
              Hoe het begon
            </h2>
            {story.length > 0 && <div className="mt-6 space-y-5 text-[17px] leading-relaxed text-muted" data-id="element-5570" data-mp="l5vkhzrh6ll2">
                {story.map((p, i) => <p key={`${i}-${p.slice(0, 24)}`} data-id="element-5571" data-mp="q22sdy3fm75n" data-mpi={i}>{fill(p)}</p>)}
              </div>}
            {closing && <p className="mt-8 type-title italic text-ink" data-id="element-5572" data-mp="g5q094njc1d0">{fill(closing)}</p>}
          </div>

          <div className="lg:col-span-5" data-id="element-5573" data-mp="2f4cp2n4z38s">
            {/* Getal + uitleg lezen als één zin: "25 jaar in de huidzorg". */}
            <ul className="grid gap-6 sm:grid-cols-2 lg:grid-cols-1 lg:gap-0 lg:divide-y lg:divide-ink/15" data-id="element-5574" data-mp="76mgdygjnhxg">
              {facts.map(f => <li key={f.label} className="lg:flex lg:items-baseline lg:gap-5 lg:py-5 lg:first:pt-0" data-id="element-5575" data-mp="3ojzsb5m23vh7">
                  <span className="block type-sub text-ink lg:w-40 lg:shrink-0" data-id="element-5576" data-mp="3tit2op61r6d5">{f.value}</span>
                  <span className="mt-1.5 block text-[14px] leading-snug text-muted lg:mt-0" data-id="element-5577" data-mp="3fnqq5j6p2ird">{f.label}</span>
                </li>)}
            </ul>
            {credentials.length > 0 && <div className="mt-8 border-t border-ink/15 pt-6" data-id="element-5578" data-mp="310ij8ukf4zl7">
                <h3 className="text-[14px] text-muted" data-id="element-5579" data-mp="2w15olnnb2o82">{fill(copy.credentials.heading)}</h3>
                <ul className="mt-3 space-y-2" data-id="element-5580" data-mp="3a05bapr9ct1l">
                  {credentials.map(c => <li key={c} className="text-[15px] leading-snug text-ink" data-id="element-5581" data-mp="35wz8xt79nm0e">
                      {fill(c)}
                    </li>)}
                </ul>
              </div>}
          </div>
        </div>
        {/* Pers als bewijs bij haar verhaal, als regel onder de feiten (zonder vermeldingen niets). */}
        {hasPress() && <div className="mx-auto max-w-content px-6 lg:px-10" data-id="element-5582" data-mp="3onqeejo4us9i">
            <PressStrip className="mt-14 lg:mt-16" data-id="element-5583" data-mp="3oqctr5h9mc2a" />
          </div>}
      </section>

      {/* Waar ik voor sta: het ene klei-vlak. De foto beweegt licht binnen zijn kader. */}
      <section id="filosofie" className="scroll-mt-24 bg-clay text-shell" aria-label="Filosofie" data-id="element-5584" data-mp="1ldhjcnb1si3">
        <div className="grid lg:grid-cols-12" data-id="element-5585" data-mp="2vdd5xrlhm2y3">
          <figure className="flex flex-col justify-center px-6 py-20 sm:px-10 lg:col-span-7 lg:py-28 lg:pl-10 lg:pr-16 xl:pl-[max(2.5rem,calc((100vw-80rem)/2+2.5rem))]" data-id="element-5586" data-mp="30a3l8cpgwuig">
            <p className="text-[14px] text-shell" data-id="element-5587" data-mp="34adp9jtp9auo">{fill(copy.philosophy.eyebrow)}</p>
            <blockquote className="mt-5 max-w-2xl type-section" data-id="element-5588" data-mp="3951i1uvqkrf2">
              “{fill(copy.philosophy.claim)}.”
            </blockquote>
            <p className="mt-8 max-w-xl text-[17px] leading-relaxed text-shell" data-id="element-5589" data-mp="3j0uuzfddamxd">{fill(copy.philosophy.body)}</p>
            <figcaption className="mt-8 type-title italic text-blush" data-id="element-5590" data-mp="3nzxxas1yqf1n">{founder.firstName}</figcaption>
          </figure>
          <ParallaxMedia media={atWork} sizes="(min-width: 1024px) 42vw, 100vw" range={40} className="aspect-[4/5] w-full sm:aspect-[16/11] lg:col-span-5 lg:aspect-auto lg:h-full lg:min-h-[640px]" data-id="element-5591" data-mp="3s0hvlyy9vopd" />
        </div>
      </section>

      <AboutShelf copy={copy.shelf} data-id="element-5592" data-mp="1q5an4wl68ai5" />

      {/* Kom kennismaken: het gedeelde "kom langs"-blok. Eén knop; de salonpagina en WhatsApp als rustige links. */}
      <VisitBlock titleId="about-salon" eyebrow={copy.salon.eyebrow} heading={copy.salon.heading} body={copy.salon.body} cta={copy.salon.cta} media={{
      image: placeholderImages.salonRoom,
      alt: 'Sfeerbeeld: een rustige behandelkamer met linnen in daglicht'
    }} motion="still" links={<>
            {copy.salon.salonCta && <Link to="/behandelingen" className={`${quietLink} text-ink`} data-id="element-5594" data-mp="202f0c6x1gvmt">
                {fill(copy.salon.salonCta)}
                <ArrowRightIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-5595" data-mp="1v3ve05ej9htx" />
              </Link>}
            {copy.salon.secondaryCta && <a href={salon.whatsapp} target="_blank" rel="noreferrer" className={`${quietLink} text-ink`} data-id="element-5596" data-mp="okx55vtix4bw">
                <MessageCircleIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-5597" data-mp="2jqg1f2s4mncl" />
                {fill(copy.salon.secondaryCta)}
                <span className="sr-only" data-id="element-5598" data-mp="2nt2z3kbhb2hp">{shopLabels.viaWhatsApp}</span>
              </a>}
          </>} data-id="element-5593" data-mp="1lbpor3fo1eif" />
    </>;
}